import {
  Badge,
  Button,
  Checkbox,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EditIcon, FileTextIcon, TrashIcon } from "lucide-react";
import type { PayrollListItem } from "@/modules/hr/payroll/domain/types";
import { RowActions } from "@/shared/ui/RowActions";

type PayrollTableProps = {
  data: PayrollListItem[];
  isLoading?: boolean;
  isError?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  selectedIds: string[];
  onSelectedIdsChange: (ids: string[]) => void;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
  onSlip: (row: PayrollListItem) => void;
  onEdit: (row: PayrollListItem) => void;
  onHistory: () => void;
  /** Draft → ຢືນຢັນ (confirmed) */
  onConfirm?: (row: PayrollListItem) => void;
  /** Confirmed → ຊຳລະ (paid) */
  onMarkPaid?: (row: PayrollListItem) => void;
  /** Confirmed → ຮ່າງ (draft) — ປົດຢືນຢັນ */
  onUnconfirm?: (row: PayrollListItem) => void;
  /** Bulk: set selected drafts to confirmed */
  onBulkConfirm?: (ids: string[]) => void;
  /** Bulk: set selected confirmed to paid */
  onBulkMarkPaid?: (ids: string[]) => void;
  /** Bulk: revert selected confirmed to draft */
  onBulkUnconfirm?: (ids: string[]) => void;
  /** Bulk: delete selected drafts only */
  onBulkDelete?: (ids: string[]) => void;
  /** Delete row — only shown for draft */
  onDelete?: (row: PayrollListItem) => void;
};

const statusLabel: Record<PayrollListItem["status"], string> = {
  draft: "ຮ່າງ",
  confirmed: "ຢືນຢັນ",
  paid: "ຊຳລະແລ້ວ",
};

export function PayrollTable({
  data,
  isLoading,
  isError,
  offset,
  limit,
  totalCount,
  selectedIds,
  onSelectedIdsChange,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onSlip,
  onEdit,
  onHistory,
  onConfirm,
  onMarkPaid,
  onUnconfirm,
  onBulkConfirm,
  onBulkMarkPaid,
  onBulkUnconfirm,
  onBulkDelete,
  onDelete,
}: PayrollTableProps) {
  const setSelection = (ids: string[]) => onSelectedIdsChange(ids);
  const toggleRow = (id: string) => {
    if (selectedIds.includes(id)) setSelection(selectedIds.filter((x) => x !== id));
    else setSelection([...selectedIds, id]);
  };
  const selectableIds = data.filter((r) => r.status !== "paid").map((r) => r.id);
  const allPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const somePageSelected = selectableIds.some((id) => selectedIds.includes(id));
  const toggleAll = () => {
    if (allPageSelected) setSelection(selectedIds.filter((id) => !selectableIds.includes(id)));
    else setSelection([...new Set([...selectedIds, ...selectableIds])]);
  };
  const selectedRows = data.filter((r) => selectedIds.includes(r.id));
  const canBulkConfirm =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r.status === "draft" || r.status === "confirmed") &&
    onBulkConfirm;
  const canBulkMarkPaid =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r.status === "confirmed" || r.status === "paid") &&
    onBulkMarkPaid;
  const canBulkDelete =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r.status === "draft") &&
    onBulkDelete;
  const canBulkUnconfirm =
    selectedRows.length > 0 &&
    selectedRows.every((r) => r.status === "confirmed") &&
    onBulkUnconfirm;

  const columns: TanstackReactTable.ColumnDef<PayrollListItem>[] = [
    {
      id: "select",
      header: () => (
        <div className="flex items-center justify-center rounded-md border-2 border-border bg-background">
          <Checkbox
            checked={
              selectableIds.length === 0
                ? false
                : allPageSelected
                  ? true
                  : somePageSelected
                    ? "indeterminate"
                    : false
            }
            onCheckedChange={selectableIds.length === 0 ? undefined : toggleAll}
            disabled={selectableIds.length === 0}
            aria-label="ເລືອກທັງໝົດ"
          />
        </div>
      ),
      cell: ({ row }) => {
        const isPaid = row.original.status === "paid";
        return (
          <div className="flex items-center justify-center rounded-md border-2 border-border bg-background">
            <Checkbox
              checked={selectedIds.includes(row.original.id)}
              onCheckedChange={isPaid ? undefined : () => toggleRow(row.original.id)}
              disabled={isPaid}
              aria-label={isPaid ? undefined : `ເລືອກ ${row.original.employeeName}`}
            />
          </div>
        );
      },
      size: 44,
      enableSorting: false,
    },
    createSortableColumn<PayrollListItem>("employeeName", "ພະນັກງານ", {
      size: 150,
      cell: ({ row }) => (
        <div>
          <div className="font-medium">{row.original.employeeName}</div>
          <div className="text-muted-foreground text-xs">{row.original.employeeEmail}</div>
        </div>
      ),
    }),
    createSortableColumn<PayrollListItem>("positionTitle", "ຕໍາແໜ່ງ", {
      size: 140,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {row.original.positionTitle ?? "—"}
          {row.original.departmentName != null && row.original.departmentName !== "" && (
            <span className="ml-1 text-muted-foreground text-xs">({row.original.departmentName})</span>
          )}
        </span>
      ),
    }),
    createSortableColumn<PayrollListItem>("baseSalary", "ເງິນເດືອນພື້ນຖານ", {
      size: 120,
      cell: ({ row }) => <span className="text-right tabular-nums">{row.original.baseSalary.toLocaleString()}</span>,
    }),
    {
      id: "otBonus",
      header: "OT / ໂບນັສ",
      cell: ({ row }) => <span className="text-right tabular-nums">
        {row.original.otAmount.toLocaleString() || 0} / {row.original.bonusAmount.toLocaleString() || 0}
        </span>,
      meta: { align: "right" },
      size: 140,
    },
    {
      id: "deductions",
      header: "ຫັກ",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-right tabular-nums">-{row.original.totalDeductions.toLocaleString()}</span>
      ),
      meta: { align: "right" },
      size: 100,
    },
    createSortableColumn<PayrollListItem>("netSalary", "ສຸດທິ", {
      size: 120,
      cell: ({ row }) => (
        <span className="text-right font-medium tabular-nums">
          {row.original.netSalary.toLocaleString()}
        </span>
      ),
    }),
    createSortableColumn<PayrollListItem>("status", "ສະຖານະ", {
      size: 100,
      cell: ({ row }) => (
        <Badge
          variant={
            row.original.status === "paid" ? "default" : row.original.status === "confirmed" ? "secondary" : "outline"
          }
        >
          {statusLabel[row.original.status]}
        </Badge>
      ),
    }),
    {
      id: "actions",
      header: "ການກະທຳ",
      cell: ({ row }) => {
        const r = row.original;
        if (r.status === "paid") {
          return <span className="text-muted-foreground text-sm">—</span>;
        }
        const actions: { label: string; icon?: React.ReactNode; variant?: "destructive"; onClick: () => void }[] = [
          { label: "ລາຍລະອຽດ", icon: <EditIcon className="h-4 w-4" />, onClick: () => onEdit(r) },
          { label: "ໃບ slip", icon: <FileTextIcon className="h-4 w-4" />, onClick: () => onSlip(r) },
        ];
        if (r.status === "draft" && onConfirm) {
          actions.push({ label: "ຢືນຢັນ", onClick: () => onConfirm(r) });
        }
        if (r.status === "confirmed" && onMarkPaid) {
          actions.push({ label: "ຊຳລະ", onClick: () => onMarkPaid(r) });
        }
        if (r.status === "confirmed" && onUnconfirm) {
          actions.push({ label: "ປົດຢືນຢັນ", onClick: () => onUnconfirm(r) });
        }
        if (r.status === "draft" && onDelete) {
          actions.push({ label: "ລຶບ", variant: "destructive", icon: <TrashIcon className="h-4 w-4" />, onClick: () => onDelete(r) });
        }
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 80,
    },
  ];

  return (
    <>
      {selectedIds.length > 0 && (
        <div className="mb-4 flex flex-wrap items-center gap-2">
              <span className="text-muted-foreground text-sm">
                ເລືອກ {selectedIds.length} ລາຍການ
              </span>
              {canBulkConfirm && (
                <Button size="sm" onClick={() => onBulkConfirm(selectedIds)}>
                  ຢືນຢັນທີ່ເລືອກ
                </Button>
              )}
              {canBulkMarkPaid && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => onBulkMarkPaid(selectedIds)}
                >
                  ຊຳລະທີ່ເລືອກ
                </Button>
              )}
              {canBulkUnconfirm && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => onBulkUnconfirm(selectedIds)}
                >
                  ປົດຢືນຢັນທີ່ເລືອກ
                </Button>
              )}
              {canBulkDelete && (
                <Button
                  size="sm"
                  variant="destructive"
                  onClick={() => onBulkDelete(selectedIds)}
                >
                  ລຶບທີ່ເລືອກ
                </Button>
              )}
              <Button size="sm" variant="ghost" onClick={() => setSelection([])}>
                ລ້າງ
              </Button>
        </div>
      )}
      {isError && (
          <p className="text-destructive text-sm">ເກີດຂໍ້ຜິດພາດໃນການໂຫຼດຂໍ້ມູນ.</p>
        )}
        {!isError && (
          <DataTable<PayrollListItem, unknown>
            noDataMessage="ບໍ່ພົບຂໍ້ມູນເງິນເດືອນ."
            isLoading={isLoading}
            columns={columns}
            data={data}
            offset={offset}
            limit={limit}
            totalCount={totalCount}
            onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={
              onSortingChange
                ? (sorting) => {
                    const s = sorting[0];
                    if (s?.id) onSortingChange(s.id, !!s.desc);
                  }
                : undefined
            }
          />
        )}
    </>
  );
}
